import { FilesetResolver, PoseLandmarker, PoseLandmarkerResult } from '@mediapipe/tasks-vision';
import { CameraTrackingState, MotionData, TrackedLandmark } from '../types';

export const POSE_CONNECTIONS: [number, number][] = [
  // Upper body
  [11, 12], // Shoulders
  [11, 13], [13, 15], // Left Arm
  [15, 17], [15, 19], // Left Hand / Fingers
  [12, 14], [14, 16], // Right Arm
  [16, 18], [16, 20], // Right Hand / Fingers
  // Torso
  [11, 23], [12, 24], [23, 24],
  // Head
  [0, 11], [0, 12],
  // Lower body (if visible)
  [23, 25], [24, 26], [25, 27], [26, 28]
];

/**
 * 1D Kalman Filter for real-time motion and gesture telemetry smoothing.
 * Filters out sensor and landmark detection jitter while dynamically preserving
 * fast musical flourishes and intentional conducting sweeps.
 */
export class KalmanFilter1D {
  private x: number = 0; // State estimate
  private p: number = 1.0; // Estimation error covariance
  private q: number; // Process noise covariance (responsiveness)
  private r: number; // Measurement noise covariance (jitter rejection)
  private isInitialized: boolean = false;

  constructor(processNoise: number = 0.025, measurementNoise: number = 0.05) {
    this.q = processNoise;
    this.r = measurementNoise;
  }

  public update(measurement: number, dynamicNoiseScale: number = 1.0): number {
    if (!this.isInitialized || isNaN(this.x)) {
      this.x = measurement;
      this.p = 1.0;
      this.isInitialized = true;
      return measurement;
    }

    const r = this.r * dynamicNoiseScale;

    // Time update (predict)
    this.p = this.p + this.q;

    // Measurement update (correct)
    const k = this.p / (this.p + r);
    this.x = this.x + k * (measurement - this.x);
    this.p = (1 - k) * this.p;

    return this.x;
  }

  public reset(val?: number) {
    if (val !== undefined && !isNaN(val)) {
      this.x = val;
      this.isInitialized = true;
    } else {
      this.isInitialized = false;
    }
    this.p = 1.0;
  }

  public getValue(): number {
    return this.x;
  }
}

export class CameraMotionService {
  private videoElement: HTMLVideoElement | null = null;
  private mediaStream: MediaStream | null = null;
  private poseLandmarker: PoseLandmarker | null = null;
  private isProcessing: boolean = false;
  private animFrameId: number | null = null;
  
  private facingMode: 'user' | 'environment' = 'user';
  private isLoading: boolean = false;
  private lastTimestamp: number = -1;
  
  // Hand tracking history for velocity & jerk
  private prevLeftWrist: { x: number; y: number; time: number } | null = null;
  private prevRightWrist: { x: number; y: number; time: number } | null = null;
  private prevBodyCenter: { x: number; y: number; time: number } | null = null;
  
  // Telemetry Kalman Filters for jitter-free, musical motion modulation
  private handXFilter = new KalmanFilter1D(0.035, 0.04); // Pitch / horizontal modulation
  private handYFilter = new KalmanFilter1D(0.03, 0.035); // Filter frequency / vertical modulation
  private leftWristXFilter = new KalmanFilter1D(0.04, 0.045);
  private leftWristYFilter = new KalmanFilter1D(0.04, 0.045);
  private rightWristXFilter = new KalmanFilter1D(0.04, 0.045);
  private rightWristYFilter = new KalmanFilter1D(0.04, 0.045);
  private bodyCenterXFilter = new KalmanFilter1D(0.015, 0.06);
  private bodyCenterYFilter = new KalmanFilter1D(0.015, 0.06);
  private handSpanFilter = new KalmanFilter1D(0.02, 0.04);
  private velocityFilter = new KalmanFilter1D(0.05, 0.05);
  private intensityFilter = new KalmanFilter1D(0.035, 0.04);
  private shoulderTiltFilter = new KalmanFilter1D(0.02, 0.06);

  // FPS calculation
  private frameCount: number = 0;
  private lastFpsTime: number = performance.now();
  private currentFps: number = 0;
  
  // Subscribers
  private motionListeners: ((data: MotionData) => void)[] = [];
  private stateListeners: ((state: CameraTrackingState) => void)[] = [];
  private countdownCompleteListeners: (() => void)[] = [];
  private countdownTimer: any = null;
  private hasCalibrated: boolean = false;
  private sensitivity: number = 1.0;

  private currentState: CameraTrackingState = {
    isStreaming: false,
    isLoadingModel: false,
    modelError: null,
    hasDetectedBody: false,
    landmarks: [],
    leftHand: null,
    rightHand: null,
    bodyCenter: null,
    handSpan: 0.5,
    velocity: 0,
    fps: 0,
    facingMode: 'user',
    countdown: null,
    isCountdownActive: false
  };

  constructor() {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.resumeCamera();
        }
      });
      window.addEventListener('focus', () => {
        this.resumeCamera();
      });
      window.addEventListener('pageshow', () => {
        this.resumeCamera();
      });
    }
  }

  public resetFilters() {
    this.handXFilter.reset();
    this.handYFilter.reset();
    this.leftWristXFilter.reset();
    this.leftWristYFilter.reset();
    this.rightWristXFilter.reset();
    this.rightWristYFilter.reset();
    this.bodyCenterXFilter.reset();
    this.bodyCenterYFilter.reset();
    this.handSpanFilter.reset();
    this.velocityFilter.reset();
    this.intensityFilter.reset();
    this.shoulderTiltFilter.reset();
    this.prevLeftWrist = null;
    this.prevRightWrist = null;
    this.prevBodyCenter = null;
  }

  public getState(): CameraTrackingState {
    return this.currentState;
  }

  public subscribeMotion(callback: (data: MotionData) => void) {
    this.motionListeners.push(callback);
    return () => {
      this.motionListeners = this.motionListeners.filter(cb => cb !== callback);
    };
  }

  public subscribeState(callback: (state: CameraTrackingState) => void) {
    this.stateListeners.push(callback);
    callback(this.currentState);
    return () => {
      this.stateListeners = this.stateListeners.filter(cb => cb !== callback);
    };
  }

  public setSensitivity(s: number) {
    this.sensitivity = Math.max(0.2, Math.min(3.0, s));
  }

  public getSensitivity(): number {
    return this.sensitivity;
  }

  public onCountdownComplete(callback: () => void) {
    this.countdownCompleteListeners.push(callback);
    return () => {
      this.countdownCompleteListeners = this.countdownCompleteListeners.filter(cb => cb !== callback);
    };
  }

  private notifyCountdownComplete() {
    const callbacks = [...this.countdownCompleteListeners];
    this.countdownCompleteListeners = [];
    callbacks.forEach(cb => {
      try { cb(); } catch(e) { console.warn('Countdown complete callback error:', e); }
    });
  }

  private notifyState(partial: Partial<CameraTrackingState>) {
    this.currentState = { ...this.currentState, ...partial };
    this.stateListeners.forEach(cb => cb(this.currentState));
  }

  private notifyMotion(data: MotionData) {
    this.motionListeners.forEach(cb => cb(data));
  }

  public async initModel(): Promise<boolean> {
    if (this.poseLandmarker) return true;
    if (this.isLoading) return false;

    this.isLoading = true;
    this.notifyState({ isLoadingModel: true, modelError: null });

    try {
      const wasmFileset = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm'
      );

      // Attempt GPU delegate first, fallback to CPU
      try {
        this.poseLandmarker = await PoseLandmarker.createFromOptions(wasmFileset, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
            delegate: 'GPU'
          },
          runningMode: 'VIDEO',
          numPoses: 1,
          minPoseDetectionConfidence: 0.4,
          minPosePresenceConfidence: 0.4,
          minTrackingConfidence: 0.4
        });
      } catch (gpuError) {
        console.warn('GPU delegate failed for PoseLandmarker, falling back to CPU:', gpuError);
        this.poseLandmarker = await PoseLandmarker.createFromOptions(wasmFileset, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
            delegate: 'CPU'
          },
          runningMode: 'VIDEO',
          numPoses: 1,
          minPoseDetectionConfidence: 0.4,
          minPosePresenceConfidence: 0.4,
          minTrackingConfidence: 0.4
        });
      }

      this.isLoading = false;
      this.notifyState({ isLoadingModel: false, modelError: null });
      return true;
    } catch (err: any) {
      console.error('Failed to load MediaPipe PoseLandmarker model:', err);
      this.isLoading = false;
      this.notifyState({ 
        isLoadingModel: false, 
        modelError: err?.message || 'Could not load camera motion tracking model.' 
      });
      return false;
    }
  }

  public async startCamera(videoEl?: HTMLVideoElement, facing: 'user' | 'environment' = 'user', triggerCountdown: boolean = false): Promise<boolean> {
    this.facingMode = facing;

    try {
      if (!this.poseLandmarker) {
        await this.initModel();
      }

      if (videoEl) {
        this.videoElement = videoEl;
      } else if (!this.videoElement) {
        this.videoElement = document.createElement('video');
        this.videoElement.playsInline = true;
        this.videoElement.muted = true;
        this.videoElement.autoplay = true;
      }

      // Stop existing stream if any
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
      }

      const constraints: MediaStreamConstraints = {
        audio: false,
        video: {
          facingMode: this.facingMode,
          width: { ideal: 640, max: 1280 },
          height: { ideal: 480, max: 720 },
          frameRate: { ideal: 30, max: 60 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.mediaStream = stream;
      this.videoElement.srcObject = stream;

      await new Promise<void>((resolve) => {
        if (!this.videoElement) return resolve();
        this.videoElement.onloadedmetadata = () => {
          this.videoElement?.play();
          resolve();
        };
      });

      this.isProcessing = true;
      this.notifyState({
        isStreaming: true,
        facingMode: this.facingMode
      });

      this.processLoop();
      
      if (triggerCountdown && !this.hasCalibrated) {
        this.startCountdown(3);
      }
      return true;
    } catch (err: any) {
      console.warn('Camera access notice:', err);
      const isPermissionDenied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError' || err?.message?.toLowerCase().includes('permission');
      const friendlyMessage = isPermissionDenied
        ? 'Camera permission was denied. Please allow camera access in your browser site settings or address bar icon, then click Retry.'
        : (err?.message || 'Webcam access unavailable. Check device permissions.');
      
      this.notifyState({
        isStreaming: false,
        modelError: friendlyMessage
      });
      return false;
    }
  }

  public async startCountdown(durationSeconds: number = 3) {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    let current = durationSeconds;
    this.notifyState({
      countdown: current,
      isCountdownActive: true,
      velocity: 0
    });

    // Quiet down audio engine motion immediately during countdown
    const quietMotion: MotionData = {
      x: 0, y: 0, z: 0, rotAlpha: 0, rotBeta: 0, rotGamma: 0, intensity: 0, jerk: 0
    };
    this.notifyMotion(quietMotion);

    this.countdownTimer = setInterval(() => {
      current--;
      if (current > 0) {
        this.notifyState({
          countdown: current,
          isCountdownActive: true,
          velocity: 0
        });
      } else if (current === 0) {
        // Show "Ready!" (0) briefly
        this.notifyState({
          countdown: 0,
          isCountdownActive: true,
          velocity: 0
        });
      } else {
        if (this.countdownTimer) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
        }
        this.hasCalibrated = true;
        this.resetFilters();
        this.notifyState({
          countdown: null,
          isCountdownActive: false
        });
        this.notifyCountdownComplete();
      }
    }, 1000);
  }

  public async resumeCamera(): Promise<void> {
    if (!this.isProcessing && !this.currentState.isStreaming) return;

    try {
      // 1. Check if media stream tracks are still active
      const hasActiveTracks = this.mediaStream && this.mediaStream.getVideoTracks().some(t => t.readyState === 'live');
      if (!hasActiveTracks && this.videoElement) {
        await this.startCamera(this.videoElement, this.facingMode, false);
        return;
      }

      // 2. Make sure video element is actively playing
      if (this.videoElement) {
        if (this.videoElement.paused) {
          try {
            await this.videoElement.play();
          } catch (e) {
            console.warn('Video resume play error:', e);
          }
        }
      }

      // 3. Reset last timestamp to prevent frame-drop freezing
      this.lastTimestamp = -1;

      // 4. Ensure process loop is ticking
      this.isProcessing = true;
      if (this.animFrameId === null) {
        this.processLoop();
      }
    } catch (err) {
      console.warn('Error resuming camera:', err);
    }
  }

  public skipCountdown() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.hasCalibrated = true;
    this.resetFilters();
    this.notifyState({
      countdown: null,
      isCountdownActive: false
    });
    this.notifyCountdownComplete();
  }

  public cancelCountdown() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.countdownCompleteListeners = [];
    this.resetFilters();
    this.notifyState({
      countdown: null,
      isCountdownActive: false
    });
  }

  public stopCamera() {
    this.cancelCountdown();
    this.hasCalibrated = false;
    this.isProcessing = false;
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }

    this.resetFilters();

    this.notifyState({
      isStreaming: false,
      hasDetectedBody: false,
      landmarks: [],
      leftHand: null,
      rightHand: null,
      bodyCenter: null,
      velocity: 0,
      countdown: null,
      isCountdownActive: false
    });

    // Quiet down audio engine motion
    const quietMotion: MotionData = {
      x: 0, y: 0, z: 0, rotAlpha: 0, rotBeta: 0, rotGamma: 0, intensity: 0, jerk: 0
    };
    this.notifyMotion(quietMotion);
  }

  public async switchFacingMode(): Promise<boolean> {
    const nextFacing = this.facingMode === 'user' ? 'environment' : 'user';
    return this.startCamera(this.videoElement || undefined, nextFacing);
  }

  public getVideoElement(): HTMLVideoElement | null {
    return this.videoElement;
  }

  private processLoop = () => {
    if (!this.isProcessing) return;

    const video = this.videoElement;
    const now = performance.now();

    // FPS Meter
    this.frameCount++;
    if (now - this.lastFpsTime >= 1000) {
      this.currentFps = Math.round((this.frameCount * 1000) / (now - this.lastFpsTime));
      this.frameCount = 0;
      this.lastFpsTime = now;
    }

    if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && this.poseLandmarker) {
      if (video.currentTime !== this.lastTimestamp) {
        this.lastTimestamp = video.currentTime;
        try {
          const result: PoseLandmarkerResult = this.poseLandmarker.detectForVideo(video, now);
          this.handlePoseResult(result, now);
        } catch (e) {
          // Ignore transient frame dropping
        }
      }
    }

    this.animFrameId = requestAnimationFrame(this.processLoop);
  };

  private handlePoseResult(result: PoseLandmarkerResult, now: number) {
    if (!result.landmarks || result.landmarks.length === 0 || result.landmarks[0].length === 0) {
      // No pose detected in this frame
      this.notifyState({
        hasDetectedBody: false,
        fps: this.currentFps
      });
      return;
    }

    const rawLandmarks = result.landmarks[0];
    const landmarks: TrackedLandmark[] = rawLandmarks.map(lm => ({
      x: lm.x,
      y: lm.y,
      z: lm.z,
      visibility: lm.visibility
    }));

    // In mirrored webcam:
    // Left wrist index 15, Left index finger index 19
    // Right wrist index 16, Right index finger index 20
    // Nose index 0
    // Shoulders: Left 11, Right 12
    const nose = rawLandmarks[0];
    const leftWrist = rawLandmarks[15] || rawLandmarks[19];
    const rightWrist = rawLandmarks[16] || rawLandmarks[20];
    const leftShoulder = rawLandmarks[11];
    const rightShoulder = rawLandmarks[12];

    // Kalman-smoothed keypoint coordinates for steady tracking
    const smoothLeftWristX = leftWrist ? this.leftWristXFilter.update(leftWrist.x) : 0.5;
    const smoothLeftWristY = leftWrist ? this.leftWristYFilter.update(leftWrist.y) : 0.5;
    const smoothRightWristX = rightWrist ? this.rightWristXFilter.update(rightWrist.x) : 0.5;
    const smoothRightWristY = rightWrist ? this.rightWristYFilter.update(rightWrist.y) : 0.5;

    // Compute Left Hand speed
    let leftSpeed = 0;
    if (leftWrist && this.prevLeftWrist) {
      const dt = Math.max(1, now - this.prevLeftWrist.time) / 1000;
      const dist = Math.hypot(smoothLeftWristX - this.prevLeftWrist.x, smoothLeftWristY - this.prevLeftWrist.y);
      leftSpeed = dist / dt;
    }
    if (leftWrist) {
      this.prevLeftWrist = { x: smoothLeftWristX, y: smoothLeftWristY, time: now };
    }

    // Compute Right Hand speed
    let rightSpeed = 0;
    if (rightWrist && this.prevRightWrist) {
      const dt = Math.max(1, now - this.prevRightWrist.time) / 1000;
      const dist = Math.hypot(smoothRightWristX - this.prevRightWrist.x, smoothRightWristY - this.prevRightWrist.y);
      rightSpeed = dist / dt;
    }
    if (rightWrist) {
      this.prevRightWrist = { x: smoothRightWristX, y: smoothRightWristY, time: now };
    }

    // Hand span (distance between left and right hands)
    let rawHandSpan = 0.5;
    if (leftWrist && rightWrist) {
      rawHandSpan = Math.min(1.0, Math.hypot(smoothLeftWristX - smoothRightWristX, smoothLeftWristY - smoothRightWristY));
    }
    const filteredHandSpan = this.handSpanFilter.update(rawHandSpan);

    // Body center and shoulder tilt
    let rawBodyCenterX = 0.5;
    let rawBodyCenterY = 0.5;
    let rawShoulderTilt = 0;
    if (leftShoulder && rightShoulder) {
      rawBodyCenterX = (leftShoulder.x + rightShoulder.x) / 2;
      rawBodyCenterY = (leftShoulder.y + rightShoulder.y) / 2;
      rawShoulderTilt = (leftShoulder.y - rightShoulder.y) * 120;
    } else if (nose) {
      rawBodyCenterX = nose.x;
      rawBodyCenterY = nose.y;
    }

    const filteredBodyCenterX = this.bodyCenterXFilter.update(rawBodyCenterX);
    const filteredBodyCenterY = this.bodyCenterYFilter.update(rawBodyCenterY);
    const filteredShoulderTilt = this.shoulderTiltFilter.update(rawShoulderTilt);

    // Calculate total movement velocity and jerk (sudden acceleration)
    const combinedSpeed = Math.max(leftSpeed, rightSpeed) * 0.7 + ((leftSpeed + rightSpeed) / 2) * 0.3;
    const rawNormalizedVelocity = Math.min(combinedSpeed, 3.5);
    const filteredVelocity = this.velocityFilter.update(rawNormalizedVelocity);
    const jerk = Math.min(Math.max(0, (combinedSpeed - 0.9) * 2.8), 4.5);

    // Synthesizing Motion Coordinates for the Generative Audio Engine:
    // Determine active controlling hand (the one moving fastest or higher up)
    let primaryHandX = smoothRightWristX;
    let primaryHandY = smoothRightWristY;
    let hasPrimaryHand = !!rightWrist;

    if (leftWrist && (!rightWrist || leftSpeed > rightSpeed * 1.2 || (smoothLeftWristY < (rightWrist ? smoothRightWristY : 1) - 0.15))) {
      primaryHandX = smoothLeftWristX;
      primaryHandY = smoothLeftWristY;
      hasPrimaryHand = true;
    } else if (!rightWrist && leftWrist) {
      primaryHandX = smoothLeftWristX;
      primaryHandY = smoothLeftWristY;
      hasPrimaryHand = true;
    }

    // Adaptive noise scaling:
    // When holding still (low velocity), increase measurement noise rejection to lock in crystal clear notes.
    // When moving quickly (gestures/conducting), decrease measurement noise for immediate, snappy response.
    const dynamicNoiseScale = Math.max(0.5, Math.min(1.8, 1.4 - (filteredVelocity * 0.6)));

    // Horizontal modulation (rotGamma: -90 to +90) -> drives Scale Note Pitch selection
    // In selfie mode: X=0 is user's right on screen if mirrored, or X=1 is user's left.
    // Normalized to -90 (far left) to +90 (far right)
    const rawHandX = hasPrimaryHand ? primaryHandX : filteredBodyCenterX;
    const smoothedHandX = this.handXFilter.update(rawHandX, dynamicNoiseScale);
    const rawRotGamma = (smoothedHandX - 0.5) * 180;
    const rotGamma = Math.max(-90, Math.min(90, rawRotGamma * this.sensitivity));

    // Vertical modulation (rotBeta: -90 to +90) -> drives Audio Filter cutoff frequency
    // Hand Y: 0.0 (high in the air) -> high cutoff/treble; 1.0 (low down) -> deep sub/warm
    const rawHandY = hasPrimaryHand ? primaryHandY : filteredBodyCenterY;
    const smoothedHandY = this.handYFilter.update(rawHandY, dynamicNoiseScale);
    // Inverting so raising hands up creates positive energetic filter frequency
    const rawRotBeta = (0.5 - smoothedHandY) * 180;
    const rotBeta = Math.max(-90, Math.min(90, rawRotBeta * this.sensitivity));

    // Intensity: 0.0 (still) to 2.0 (dynamic fast movement + wide arm span)
    const rawIntensity = Math.min(2.0, ((filteredVelocity * 0.55) + (filteredHandSpan * 0.45) + (jerk > 1.2 ? 0.3 : 0)) * this.sensitivity);
    const smoothedIntensity = this.intensityFilter.update(rawIntensity);
    const scaledJerk = jerk * this.sensitivity;

    // If countdown is active, update skeleton landmarks so user sees positioning preview,
    // but pause telemetry velocity and keep audio modulation quiet until countdown ends.
    if (this.currentState.isCountdownActive) {
      this.notifyState({
        hasDetectedBody: true,
        landmarks,
        leftHand: leftWrist ? { x: smoothLeftWristX, y: smoothLeftWristY, speed: 0 } : null,
        rightHand: rightWrist ? { x: smoothRightWristX, y: smoothRightWristY, speed: 0 } : null,
        bodyCenter: { x: filteredBodyCenterX, y: filteredBodyCenterY },
        handSpan: filteredHandSpan,
        velocity: 0,
        fps: this.currentFps
      });

      const quietMotion: MotionData = {
        x: 0,
        y: 0,
        z: 0,
        rotAlpha: 0,
        rotBeta: 0,
        rotGamma: 0,
        intensity: 0,
        jerk: 0
      };
      this.notifyMotion(quietMotion);
      return;
    }

    const motionData: MotionData = {
      x: (smoothedHandX - 0.5) * 2,
      y: (smoothedHandY - 0.5) * 2,
      z: 0,
      rotAlpha: filteredShoulderTilt,
      rotBeta: rotBeta,
      rotGamma: rotGamma,
      intensity: smoothedIntensity,
      jerk: scaledJerk
    };

    // Update state for UI visualizer and PIP HUD
    this.notifyState({
      hasDetectedBody: true,
      landmarks,
      leftHand: leftWrist ? { x: smoothLeftWristX, y: smoothLeftWristY, speed: leftSpeed } : null,
      rightHand: rightWrist ? { x: smoothRightWristX, y: smoothRightWristY, speed: rightSpeed } : null,
      bodyCenter: { x: filteredBodyCenterX, y: filteredBodyCenterY },
      handSpan: filteredHandSpan,
      velocity: filteredVelocity,
      fps: this.currentFps
    });

    // Feed to audio engine
    this.notifyMotion(motionData);
  }
}

export const cameraMotionService = new CameraMotionService();
